import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "godo_ai_inspector_mode";

interface AiInspectorContextValue {
  inspectorMode: boolean;
  setInspectorMode: (on: boolean) => void;
  toggleInspectorMode: () => void;
  captureContext: () => Record<string, unknown>;
  registerContextProvider: (fn: () => Record<string, unknown>) => () => void;
}

const AiInspectorContext = createContext<AiInspectorContextValue | null>(null);

export function AiInspectorProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [inspectorMode, setInspectorModeState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [providers, setProviders] = useState<Array<() => Record<string, unknown>>>([]);

  const setInspectorMode = useCallback((on: boolean) => {
    setInspectorModeState(on);
    try {
      localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  const toggleInspectorMode = useCallback(() => {
    setInspectorMode(!inspectorMode);
  }, [inspectorMode, setInspectorMode]);

  const registerContextProvider = useCallback((fn: () => Record<string, unknown>) => {
    setProviders((prev) => [...prev, fn]);
    return () => setProviders((prev) => prev.filter((p) => p !== fn));
  }, []);

  const captureContext = useCallback(() => {
    const merged: Record<string, unknown> = { route: location.pathname };
    for (const fn of providers) {
      Object.assign(merged, fn());
    }
    return merged;
  }, [location.pathname, providers]);

  const value = useMemo(
    () => ({
      inspectorMode,
      setInspectorMode,
      toggleInspectorMode,
      captureContext,
      registerContextProvider,
    }),
    [
      inspectorMode,
      setInspectorMode,
      toggleInspectorMode,
      captureContext,
      registerContextProvider,
    ],
  );

  return (
    <AiInspectorContext.Provider value={value}>{children}</AiInspectorContext.Provider>
  );
}

export function useAiInspector() {
  const ctx = useContext(AiInspectorContext);
  if (!ctx) throw new Error("useAiInspector outside provider");
  return ctx;
}
